import { useEffect } from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import { ArrowLeft, Ghost, Crown, Users } from 'lucide-react'
import { toast } from 'sonner'
import MainLayout from '@/components/Layout/MainLayout'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { SubscriptionTab } from '@/components/GodMode/SubscriptionTab'
import { UsersTab } from '@/components/GodMode/UsersTab'
import { godModeAccounts } from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'
import type {
  GodModeAccount,
  GodModeSubscription,
  GodModePlan,
  GodModeUser,
} from '@/types/godmode'

interface AccountShowProps {
  current_user: CurrentUser
  god_mode: boolean
  account: GodModeAccount
  subscription: GodModeSubscription | null
  plans: GodModePlan[]
  users: GodModeUser[]
}

export default function AccountShow({
  current_user,
  god_mode,
  account,
  subscription,
  plans,
  users
}: AccountShowProps) {
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props

  useEffect(() => {
    if (flash?.notice) {
      toast.success(flash.notice)
    }
  }, [flash?.notice])

  return (
    <>
      <Head title={`${account.name} - God Mode`} />
      <MainLayout
        user={current_user}
        breadcrumbs={[
          { label: 'God Mode' },
          { label: 'Accounts', href: godModeAccounts.index.path() },
          { label: account.name },
        ]}
        godMode={god_mode}
      >
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            <Link
              href={godModeAccounts.index.path()}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <ArrowLeft className="size-4" />
              Back to accounts
            </Link>

            <div className="flex items-center gap-4">
              <div className="size-16 rounded-xl overflow-hidden flex items-center justify-center bg-primary/20">
                {account.logo_url ? (
                  <img src={account.logo_url} alt={account.name} className="size-full object-cover" />
                ) : (
                  <Ghost className="size-8 text-primary" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-semibold">{account.name}</h1>
                <p className="text-muted-foreground">{account.slug}</p>
              </div>
            </div>

            <Tabs defaultValue="subscription" className="w-full">
              <TabsList>
                <TabsTrigger value="subscription" className="cursor-pointer gap-2">
                  <Crown className="size-4" />
                  Subscription
                </TabsTrigger>
                <TabsTrigger value="users" className="cursor-pointer gap-2">
                  <Users className="size-4" />
                  Users ({users.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="subscription">
                <SubscriptionTab
                  accountSlug={account.slug}
                  subscription={subscription}
                  plans={plans}
                />
              </TabsContent>

              <TabsContent value="users">
                <UsersTab users={users} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </MainLayout>
    </>
  )
}
